import { createAsyncThunk } from '@reduxjs/toolkit';
import { getConnectedDevices, getNodeDataByTimestamps } from 'services/devices/devices.service';
import {
  computeDataRangeFromDevices,
  mergeDataPointsIntoDevices,
} from '../../utils/integrationDataUtils';
import { normalizeConnectedDevices } from '../../utils/integrationNormalize';
import {
  setLinkingGroupId,
  setSelectedDeviceIndex,
  setUpdatedSelectedDevice,
} from '../slices/deviceSlice';
import { rejectServerError } from './thunkUtils';

// Fetch connected devices and normalize location fields.
export const fetchConnectedDevices = createAsyncThunk(
  'integration/fetchConnectedDevices',
  async (_, { rejectWithValue, getState }) => {
    const res = await getConnectedDevices();
    if (res?.status !== 200) return rejectServerError(rejectWithValue);
    const { dataStartTime, dataEndTime } = computeDataRangeFromDevices(
      null,
      getState()?.integration?.dataStartTime,
      getState()?.integration?.dataEndTime,
    );
    return {
      systemCount: res?.data?.subCounts?.[0] || null,
      dataStartTime,
      dataEndTime,
    };
  },
);

// Fetch device data history for the selected time range.
export const fetchDeviceDataByTimestamps = createAsyncThunk(
  'integration/fetchDeviceDataByTimestamps',
  async (
    { startTime, endTime, checkAgainstNodeTsInstead = false },
    { rejectWithValue, getState },
  ) => {
    // Match the historical device data query used by the old device hook.
    // Forward `checkAgainstNodeTsInstead` to the service so longer ranges can be requested when needed.
    if (process.env.NODE_ENV === 'development') {
      console.debug('Requesting device history', { startTime, endTime, checkAgainstNodeTsInstead });
    }
    const res = await getNodeDataByTimestamps(
      startTime,
      endTime,
      undefined,
      checkAgainstNodeTsInstead,
    );
    if (res?.status !== 200) return rejectServerError(rejectWithValue);
    const existing = getState()?.integration?.connectedDevices || [];
    const merged = mergeDataPointsIntoDevices(existing, res?.data?.result || []);
    const prevStart = getState()?.integration?.dataStartTime;
    const prevEnd = getState()?.integration?.dataEndTime;
    const dataStartTime =
      typeof startTime === 'number'
        ? Math.min(prevStart ?? startTime, startTime)
        : (prevStart ?? null);
    const dataEndTime =
      typeof endTime === 'number' ? Math.max(prevEnd ?? endTime, endTime) : (prevEnd ?? null);
    return {
      connectedDevices: merged,
      dataStartTime,
      dataEndTime,
    };
  },
);

export const setSelectedDevice = createAsyncThunk(
  'device/setSelectedDevice',
  ({ index, id = -1 }, { getState, dispatch }) => {
    let deviceIndex = index;

    if (id >= 0) {
      const state = getState();
      const connectedDevices = state.integration.connectedDevices || [];

      const foundDeviceIndex = connectedDevices.findIndex((device) => device.id === id); // Find device location in array by ID (returns -1 if not found)

      if (foundDeviceIndex !== -1) {
        deviceIndex = foundDeviceIndex;
      }
    }

    dispatch(setSelectedDeviceIndex(deviceIndex));
    dispatch(setUpdatedSelectedDevice());
    // Explicitly clear group linkage: this thunk dispatches setSelectedDeviceIndex, not
    // setSelectedDeviceId, so the reducer's auto-null on setSelectedDeviceId won't fire here.
    dispatch(setLinkingGroupId(null));
  },
);
